import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Compass, ArrowRight, Newspaper, Search, Home as HomeIcon, DollarSign, MessageCircle } from 'lucide-react';
import { useT } from '../i18n';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotFoundPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { pathname } = useLocation();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    trackEvent('page_not_found', { page_path: pathname });
    fetch(`${API}/api/magazine/articles?page=1&page_size=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setArticles(d?.items || []))
      .catch(() => {});
  }, [pathname]);

  const quick = [
    { to: '/', label: isEs ? 'Inicio' : 'Home', icon: HomeIcon },
    { to: '/index-ai', label: 'Index AI Scanner™', icon: Search },
    { to: '/magazine', label: 'Magazine', icon: Newspaper },
    { to: '/pricing', label: isEs ? 'Precios' : 'Pricing', icon: DollarSign },
    { to: '/contact', label: isEs ? 'Contacto' : 'Contact', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pt-40 pb-24" data-testid="not-found-page">
      <SEO path={pathname} title={isEs ? 'Sigue explorando | Kitxens' : 'Keep exploring | Kitxens'} noindex />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <p className="inline-flex items-center gap-2 rounded-full bg-[#F77F11]/15 text-[#FF9B4A] text-[11px] tracking-[0.2em] uppercase font-bold px-3.5 py-1.5">
          <Compass className="w-3.5 h-3.5" /> {isEs ? 'Sigue explorando' : 'Keep exploring'}
        </p>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05] max-w-2xl">
          {isEs ? 'Justo lo que buscabas' : 'Just what you were looking for'}<br />
          <span className="text-[#F77F11]">{isEs ? 'está muy cerca.' : 'is right nearby.'}</span>
        </h1>
        <p className="mt-4 text-neutral-400 text-base max-w-xl leading-relaxed">
          {isEs
            ? 'Te dejamos aquí lo último de nuestro Knowledge Center y los accesos más útiles para seguir.'
            : "Here's the latest from our Knowledge Center and the most useful shortcuts to continue."}
        </p>

        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="nf-quick-links">
          {quick.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-[#F77F11] hover:text-[#FF9B4A] px-4.5 py-2.5 px-5 text-[13.5px] font-semibold text-neutral-200 transition-colors">
              <Icon className="w-4 h-4" /> {label}
            </Link>
          ))}
        </div>

        {articles.length > 0 && (
          <div className="mt-14" data-testid="nf-latest-articles">
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#FF9B4A] flex items-center gap-2">
              <Newspaper className="w-3.5 h-3.5" /> {isEs ? 'Lo último del Magazine' : 'Latest from the Magazine'}
            </p>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((a) => (
                <Link key={a.slug} to={`/magazine/${a.slug}`}
                  className="group rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[#F77F11]/50 transition-colors"
                  data-testid={`nf-article-${a.slug}`}>
                  {a.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={a.cover_image.startsWith('/') ? `${API}${a.cover_image}` : a.cover_image} alt={a.cover_alt || a.title}
                        loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-5">
                    <p className="text-[15px] font-bold leading-snug group-hover:text-[#FF9B4A] transition-colors">{a.title}</p>
                    <p className="mt-2 text-[12.5px] text-neutral-400 line-clamp-2">{a.excerpt}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#F77F11]">
                      {isEs ? 'Leer' : 'Read'} <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 rounded-3xl border border-[#F77F11]/30 bg-[#F77F11]/[0.06] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-[17px] font-bold">{isEs ? '¿Tu restaurante aparece cuando la IA responde?' : 'Does your restaurant show up when AI answers?'}</p>
            <p className="mt-1 text-[13.5px] text-neutral-400">{isEs ? 'Escanea tu web gratis en 30 segundos.' : 'Scan your website free in 30 seconds.'}</p>
          </div>
          <Link to="/index-ai" className="inline-flex items-center gap-2 shrink-0 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[14px] font-bold transition-colors" data-testid="nf-scan-cta">
            {isEs ? 'Escanear gratis' : 'Scan free'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
